import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../contexts/AuthContext';
import { formatDateBR } from '../helpers/formatDate';
import InfoFluxoAtendimento from '../components/InfoFluxoAtendimento';
import ChecklistsDoChamado from '../components/insumos/ChecklistsDoChamado';

// Status que encerram o chamado: e nesses que o checklist de insumos marcado
// como "encerrar junto" e finalizado/cancelado antes de mudar o status.
const STATUS_QUE_ENCERRAM = ['resolvido', 'fechado', 'cancelado'];

const STATUS_LABEL = {
  aberto: 'Aberto',
  em_atendimento: 'Em atendimento',
  aguardando_usuario: 'Aguardando usuario',
  resolvido: 'Resolvido',
  fechado: 'Fechado',
  cancelado: 'Cancelado',
};

const PRIORIDADE_LABEL = {
  baixa: 'Baixa',
  media: 'Media',
  alta: 'Alta',
  urgente: 'Urgente',
};

function formatHoraBR(valor) {
  if (!valor) return '';
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function formatDataHoraBR(valor) {
  const data = formatDateBR(valor);
  const hora = formatHoraBR(valor);
  if (!hora) return data;
  return `${data} ${hora}`;
}

function autorMensagem(m) {
  if (m.direcao === 'in') return 'Cliente';
  if (m.autor === 'bot') return 'Bot';
  return m.autor || 'Atendente';
}

const SLA_CORES = {
  verde:    { bg: 'rgba(40, 167, 69, 0.18)',  fg: '#28a745', borda: 'rgba(40, 167, 69, 0.55)' },
  amarelo:  { bg: 'rgba(255, 193, 7, 0.18)',  fg: '#d4a000', borda: 'rgba(255, 193, 7, 0.55)' },
  vermelho: { bg: 'rgba(220, 53, 69, 0.20)',  fg: '#dc3545', borda: 'rgba(220, 53, 69, 0.55)' },
};

function formatMinutos(min) {
  if (min == null) return '-';
  const m = Math.max(0, Math.round(min));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

// Dot de semaforo SLA. `kind` so muda o label no tooltip ('1ª resp.' / 'Solucao').
// Sem SLA configurado para a prioridade -> retorna apenas tracinho neutro.
function SlaDot({ info, kind }) {
  if (!info) return <span style={{ color: 'var(--portal-muted)' }}>-</span>;
  const titulo = kind === 'ttfr' ? '1a resposta' : 'Solucao';
  if (!info.limite_min) {
    return (
      <span title={`${titulo}: sem SLA configurado`} style={{ color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
        -
      </span>
    );
  }
  const cor = info.cor ? SLA_CORES[info.cor] : null;
  const decorrido = formatMinutos(info.decorrido_min);
  const limite = formatMinutos(info.limite_min);
  const sufixo = info.concluido ? ' (concluido)' : '';
  const titleTxt = `${titulo}: ${decorrido} / ${limite}${sufixo}`;
  if (!cor) {
    return <span title={titleTxt} style={{ color: 'var(--portal-muted)' }}>-</span>;
  }
  return (
    <span
      title={titleTxt}
      style={{
        display: 'inline-block', width: 12, height: 12, borderRadius: '50%',
        background: cor.fg, border: `1px solid ${cor.borda}`,
        boxShadow: info.cor === 'vermelho' ? `0 0 0 2px ${cor.bg}` : 'none',
      }}
    />
  );
}

const PRIORIDADE_CORES = {
  baixa:   '#5E7A82',
  media:   '#2A8A9C',
  alta:    '#fe8d25',
  urgente: '#dc3545',
};

const STATUS_ABERTOS_SET = new Set(['aberto', 'em_atendimento', 'aguardando_usuario']);

// Chamado vencido = tem prazo, o prazo ja passou e ainda esta em aberto.
// Chamados encerrados nunca contam como vencidos.
function isVencido(c) {
  if (!c?.data_limite || !STATUS_ABERTOS_SET.has(c.status)) return false;
  const d = new Date(c.data_limite);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}

// Converte uma data para o formato aceito por <input type="datetime-local">
// (YYYY-MM-DDTHH:mm) no fuso local, sem sufixo de timezone.
function paraDatetimeLocal(valor) {
  if (!valor) return '';
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return '';
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

// Valor sentinela do filtro de status: agrupa os 3 status nao-encerrados
// (aberto + em_atendimento + aguardando_usuario). E o filtro padrao ao abrir.
const FILTRO_EM_ABERTO = 'em_aberto';

const BOTOES_POR_STATUS = {
  aberto: [
    { status: 'em_atendimento', label: 'Assumir e iniciar', cor: '#2A8A9C', exigeMotivo: false, assumir: true },
    { status: 'cancelado',      label: 'Cancelar',          cor: '#6c757d', exigeMotivo: true,  assumir: false },
  ],
  em_atendimento: [
    { status: 'aguardando_usuario', label: 'Aguardando usuario', cor: '#5E7A82', exigeMotivo: false, assumir: false },
    { status: 'resolvido',          label: 'Resolver',           cor: '#28a745', exigeMotivo: true,  assumir: false },
    { status: 'fechado',            label: 'Fechar',             cor: '#6c757d', exigeMotivo: true,  assumir: false },
    { status: 'cancelado',          label: 'Cancelar',           cor: '#dc3545', exigeMotivo: true,  assumir: false },
  ],
  aguardando_usuario: [
    { status: 'em_atendimento', label: 'Retomar atendimento', cor: '#2A8A9C', exigeMotivo: false, assumir: false },
    { status: 'resolvido',       label: 'Resolver',            cor: '#28a745', exigeMotivo: true,  assumir: false },
    { status: 'fechado',         label: 'Fechar',              cor: '#6c757d', exigeMotivo: true,  assumir: false },
    { status: 'cancelado',       label: 'Cancelar',            cor: '#dc3545', exigeMotivo: true,  assumir: false },
  ],
};

// Select inline para alterar a prioridade. Salva imediatamente via PATCH.
// onSaved recebe o chamado atualizado para refresh local.
function SeletorPrioridade({ chamado, onSaved }) {
  const [salvando, setSalvando] = useState(false);
  const cor = PRIORIDADE_CORES[chamado.prioridade] || '#5E7A82';
  const trocar = async (nova) => {
    if (nova === chamado.prioridade || salvando) return;
    setSalvando(true);
    try {
      const res = await axios.patch(`${API}/chamados/${chamado.id}/prioridade`, { prioridade: nova });
      onSaved?.(res.data?.data || null);
    } catch (e) { /* silencioso, polling proximo corrige */ }
    finally { setSalvando(false); }
  };
  return (
    <select
      value={chamado.prioridade}
      disabled={salvando}
      onChange={(e) => trocar(e.target.value)}
      title="Alterar prioridade"
      style={{
        background: `${cor}22`, color: cor, border: `1px solid ${cor}55`,
        borderRadius: 8, padding: '2px 6px', fontSize: '0.78rem', fontWeight: 600,
        cursor: 'pointer', minWidth: 90,
      }}
    >
      {Object.entries(PRIORIDADE_LABEL).map(([k, v]) => (
        <option key={k} value={k}>{v}</option>
      ))}
    </select>
  );
}

const NIVEIS = ['N1', 'N2', 'N3'];

const NIVEL_CORES = {
  N1: '#2A8A9C',
  N2: '#fe8d25',
  N3: '#8e44ad',
};

// Select inline para escalar/rebaixar o nivel (N1/N2/N3). Salva na hora via
// PATCH; o backend registra a troca como comentario do chamado.
function SeletorNivel({ chamado, onSaved }) {
  const [salvando, setSalvando] = useState(false);
  const nivel = chamado.nivel || 'N1';
  const cor = NIVEL_CORES[nivel] || '#5E7A82';
  const trocar = async (novo) => {
    if (novo === nivel || salvando) return;
    setSalvando(true);
    try {
      const res = await axios.patch(`${API}/chamados/${chamado.id}/nivel`, { nivel: novo });
      onSaved?.(res.data?.data || null);
    } catch (e) { /* silencioso, polling proximo corrige */ }
    finally { setSalvando(false); }
  };
  return (
    <select
      value={nivel}
      disabled={salvando}
      onChange={(e) => trocar(e.target.value)}
      title="Alterar nivel do chamado"
      style={{
        background: `${cor}22`, color: cor, border: `1px solid ${cor}55`,
        borderRadius: 8, padding: '2px 6px', fontSize: '0.78rem', fontWeight: 600,
        cursor: 'pointer', minWidth: 56,
      }}
    >
      {NIVEIS.map((n) => <option key={n} value={n}>{n}</option>)}
    </select>
  );
}

// Select inline para atribuir o atendente na tabela (mesmo PATCH do detalhe).
// Nao muda o status: so define quem e o responsavel.
function SeletorAtendente({ chamado, atendentes, onSaved }) {
  const [salvando, setSalvando] = useState(false);
  const atual = chamado.atendente || '';
  const trocar = async (novo) => {
    if (novo === atual || salvando) return;
    setSalvando(true);
    try {
      const res = await axios.patch(`${API}/chamados/${chamado.id}/atendente`, { atendente: novo || null });
      onSaved?.(res.data?.data || null);
    } catch (e) { /* silencioso, o select volta ao valor atual */ }
    finally { setSalvando(false); }
  };
  return (
    <select
      value={atual}
      disabled={salvando}
      onChange={(e) => trocar(e.target.value)}
      title="Atribuir atendente"
      style={{
        background: 'transparent', color: atual ? 'var(--portal-text)' : 'var(--portal-muted)',
        border: '1px solid var(--portal-border)', borderRadius: 8,
        padding: '2px 6px', fontSize: '0.78rem', maxWidth: 140, cursor: 'pointer',
      }}
    >
      <option value="">- sem atendente -</option>
      {atendentes.map((a) => <option key={a.nome} value={a.nome}>{a.nome}</option>)}
      {/* mantem o atual mesmo se ele saiu do cadastro */}
      {atual && !atendentes.some((a) => a.nome === atual) && <option value={atual}>{atual}</option>}
    </select>
  );
}

// Modal de cadastro dos atendentes que aparecem nos seletores de atribuicao.
// Com o cadastro vazio os seletores listam todos os usuarios ativos.
function GerenciarAtendentes({ onClose, onChanged }) {
  const [cadastrados, setCadastrados] = useState([]);
  const [disponiveis, setDisponiveis] = useState([]);
  const [novo, setNovo] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const aplicar = (data) => {
    setCadastrados(data?.cadastrados || []);
    setDisponiveis(data?.disponiveis || []);
  };

  useEffect(() => {
    axios.get(`${API}/chamados/atendentes/cadastro`)
      .then((r) => aplicar(r.data?.data))
      .catch((err) => setErro(err.response?.data?.message || 'Nao foi possivel carregar os atendentes.'))
      .finally(() => setCarregando(false));
  }, []);

  const cadastrar = async () => {
    if (!novo) return;
    setSalvando(true);
    setErro('');
    try {
      const res = await axios.post(`${API}/chamados/atendentes`, { nome: novo });
      aplicar(res.data?.data);
      setNovo('');
      onChanged?.();
    } catch (err) {
      setErro(err.response?.data?.message || 'Nao foi possivel cadastrar o atendente.');
    } finally {
      setSalvando(false);
    }
  };

  const remover = async (a) => {
    if (!window.confirm(`Remover ${a.nome} dos atendentes? Os chamados ja atribuidos a ele continuam com ele.`)) return;
    setSalvando(true);
    setErro('');
    try {
      const res = await axios.delete(`${API}/chamados/atendentes/${a.id}`);
      aplicar(res.data?.data);
      onChanged?.();
    } catch (err) {
      setErro(err.response?.data?.message || 'Nao foi possivel remover o atendente.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(8, 23, 43, 0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="content-card"
        style={{
          width: 'min(480px, 96vw)', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden',
        }}
      >
        <header style={{
          padding: '16px 20px', borderBottom: '1px solid var(--portal-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--portal-text)' }}>
            <i className="bi bi-headset me-2" />Atendentes
          </div>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            <i className="bi bi-x-lg" />
          </button>
        </header>

        <div style={{ padding: '16px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <select
              className="form-select"
              value={novo}
              onChange={(e) => { setNovo(e.target.value); setErro(''); }}
              disabled={salvando || carregando}
              style={{ fontSize: '0.85rem' }}
            >
              <option value="">Selecione um usuario...</option>
              {disponiveis.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <button
              type="button"
              className="btn btn-sm"
              disabled={!novo || salvando}
              onClick={cadastrar}
              style={{ background: '#2A8A9C', color: '#fff', border: 'none', whiteSpace: 'nowrap' }}
            >
              <i className="bi bi-plus-lg me-1" />Cadastrar
            </button>
          </div>

          {erro && (
            <div className="feedback-banner feedback-banner--error" style={{ fontSize: '0.82rem' }}>{erro}</div>
          )}

          {carregando ? (
            <div style={{ fontSize: '0.85rem', color: 'var(--portal-muted)' }}>Carregando...</div>
          ) : cadastrados.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {cadastrados.map((a) => (
                <div
                  key={a.id}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                    padding: '8px 12px', borderRadius: 10,
                    border: '1px solid var(--portal-border)', background: 'var(--portal-surface-soft)',
                  }}
                >
                  <span style={{ fontSize: '0.88rem', color: 'var(--portal-text)' }}>{a.nome}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    disabled={salvando}
                    onClick={() => remover(a)}
                    title="Remover dos atendentes"
                    style={{ fontSize: '0.72rem', padding: '2px 8px' }}
                  >
                    <i className="bi bi-trash me-1" />Remover
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: '0.82rem', color: 'var(--portal-muted)' }}>
              Nenhum atendente cadastrado. Enquanto a lista estiver vazia, a atribuicao
              mostra todos os usuarios ativos do portal.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Chamados() {
  const { temPermissao, user } = useAuth();
  const navigate = useNavigate();
  const [chamados, setChamados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [statusFiltro, setStatusFiltro] = useState(FILTRO_EM_ABERTO);
  const [prioridadeFiltro, setPrioridadeFiltro] = useState('');
  const [nivelFiltro, setNivelFiltro] = useState('');

  // Modal de historico de conversa
  const [histChamado, setHistChamado] = useState(null);
  const [histMensagens, setHistMensagens] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histErro, setHistErro] = useState('');

  // Modal de detalhes do chamado (descricao + motivo + comentarios)
  const [detChamado, setDetChamado] = useState(null);
  const [detLoading, setDetLoading] = useState(false);
  const [detErro, setDetErro] = useState('');

  // Modal de novo chamado (manual)
  const podeAbrir = temPermissao('ti:chamados', 'ti:chamados:abrir');
  // So a equipe (ti:chamados) abre chamado em nome de outra pessoa; quem so tem
  // ti:chamados:abrir registra sempre em nome proprio.
  const podeInformarSolicitante = temPermissao('ti:chamados');
  const [novoAberto, setNovoAberto] = useState(false);
  const [novoForm, setNovoForm] = useState({
    titulo: '', descricao: '', categoria: '', prioridade: 'urgente', filial: '',
    solicitante: '', contato_id: null, data_limite: '',
  });
  const [novoCategorias, setNovoCategorias] = useState([]);
  const [novoFiliais, setNovoFiliais] = useState([]);
  const [novoContatos, setNovoContatos] = useState([]);
  const [novoSalvando, setNovoSalvando] = useState(false);
  const [novoErro, setNovoErro] = useState('');

  // Checklist de insumos (aba Insumos) integrado ao chamado: abrir junto com o
  // chamado e encerrar junto ao fechar. So para quem tem acesso a Insumos.
  const podeInsumos = temPermissao('ti:insumos');
  const [novoComChecklist, setNovoComChecklist] = useState(false);
  const checklistsRef = useRef(null);

  const [acaoMotivo, setAcaoMotivo] = useState('');
  const [acaoPrioridade, setAcaoPrioridade] = useState('');
  const [acaoSalvando, setAcaoSalvando] = useState(false);
  const [acaoErro, setAcaoErro] = useState('');
  const [novoComentario, setNovoComentario] = useState('');
  const [comentarioSalvando, setComentarioSalvando] = useState(false);

  // Edicao inline do titulo
  const [editandoTitulo, setEditandoTitulo] = useState(false);
  const [tituloRascunho, setTituloRascunho] = useState('');
  const [tituloSalvando, setTituloSalvando] = useState(false);
  const [tituloErro, setTituloErro] = useState('');

  // Edicao inline de descricao
  const [editandoDesc, setEditandoDesc] = useState(false);
  const [descRascunho, setDescRascunho] = useState('');
  const [descSalvando, setDescSalvando] = useState(false);
  const [descErro, setDescErro] = useState('');

  // Edicao inline do prazo de entrega
  const [editandoPrazo, setEditandoPrazo] = useState(false);
  const [prazoRascunho, setPrazoRascunho] = useState('');
  const [prazoSalvando, setPrazoSalvando] = useState(false);
  const [prazoErro, setPrazoErro] = useState('');

  // Atribuicao de atendente (sem iniciar o chamado)
  const [atendentes, setAtendentes] = useState([]);
  const [editandoAtendente, setEditandoAtendente] = useState(false);
  const [atendenteRascunho, setAtendenteRascunho] = useState('');
  const [atendenteSalvando, setAtendenteSalvando] = useState(false);
  const [atendenteErro, setAtendenteErro] = useState('');
  const [gerenciarAtendentes, setGerenciarAtendentes] = useState(false);

  // Contador de chamados vencidos do proprio atendente (+ sem atendente).
  // ids alimenta o filtro "so vencidos" pra lista bater com o contador.
  const [vencidosIds, setVencidosIds] = useState(() => new Set());
  const [soVencidos, setSoVencidos] = useState(false);
  const podeVerVencidos = temPermissao('ti:chamados');

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/chamados`)
      .then((res) => setChamados(res.data?.data || []))
      .catch(() => setErro('Nao foi possivel carregar os chamados.'))
      .finally(() => setLoading(false));
  }, []);

  // Vencidos do proprio atendente (atribuidos a ele + sem atendente) pro contador.
  const carregarVencidos = React.useCallback(() => {
    if (!podeVerVencidos) { setVencidosIds(new Set()); return; }
    axios.get(`${API}/chamados/vencidos`)
      .then((res) => setVencidosIds(new Set((res.data?.data || []).map((c) => c.id))))
      .catch(() => { /* contador e nice-to-have */ });
  }, [podeVerVencidos]);

  useEffect(() => { carregarVencidos(); }, [carregarVencidos]);

  // Lista dos seletores de atribuicao (tabela e detalhe). Recarrega quando o
  // cadastro de atendentes muda.
  const carregarAtendentes = React.useCallback(() => {
    if (!podeVerVencidos) return;
    axios.get(`${API}/chamados/atendentes`)
      .then((r) => setAtendentes(r.data?.data || []))
      .catch(() => { /* seletor degrada pra lista vazia */ });
  }, [podeVerVencidos]);

  useEffect(() => { carregarAtendentes(); }, [carregarAtendentes]);

  const chamadosFiltrados = chamados
    .filter((c) => {
      if (soVencidos && !vencidosIds.has(c.id)) return false;
      if (statusFiltro === FILTRO_EM_ABERTO) {
        if (!STATUS_ABERTOS_SET.has(c.status)) return false;
      } else if (statusFiltro && c.status !== statusFiltro) {
        return false;
      }
      if (prioridadeFiltro && c.prioridade !== prioridadeFiltro) return false;
      if (nivelFiltro && (c.nivel || 'N1') !== nivelFiltro) return false;
      return true;
    })
    // Vencidos sobem pro topo (prazo mais antigo primeiro); o resto mantem a
    // ordem do backend (created_at desc) gracas ao sort estavel.
    .sort((a, b) => {
      const va = isVencido(a) ? 0 : 1;
      const vb = isVencido(b) ? 0 : 1;
      if (va !== vb) return va - vb;
      if (va === 0) return new Date(a.data_limite) - new Date(b.data_limite);
      return 0;
    });

  const abrirHistorico = async (chamado) => {
    setHistChamado(chamado);
    setHistMensagens([]);
    setHistErro('');
    setHistLoading(true);
    try {
      const res = await axios.get(`${API}/chamados/${chamado.id}/historico-conversa`);
      setHistMensagens(res.data?.data?.mensagens || []);
    } catch (err) {
      setHistErro(err.response?.data?.message || 'Nao foi possivel carregar o historico.');
    } finally {
      setHistLoading(false);
    }
  };

  const abrirDetalhes = async (chamado) => {
    setDetChamado({ ...chamado, comentarios: [] }); // mostra header rapido
    setDetErro('');
    setDetLoading(true);
    try {
      const res = await axios.get(`${API}/chamados/${chamado.id}`);
      setDetChamado(res.data?.data || null);
    } catch (err) {
      setDetErro(err.response?.data?.message || 'Nao foi possivel carregar os detalhes.');
    } finally {
      setDetLoading(false);
    }
  };

  const salvarAtendente = async (chamadoId) => {
    setAtendenteSalvando(true);
    setAtendenteErro('');
    try {
      const res = await axios.patch(`${API}/chamados/${chamadoId}/atendente`, {
        atendente: (atendenteRascunho || '').trim() || null,
      });
      const atualizado = res.data?.data || null;
      if (atualizado) {
        setDetChamado((prev) => (prev ? { ...prev, atendente: atualizado.atendente } : prev));
        setChamados((prev) => prev.map((x) => (x.id === atualizado.id ? { ...x, atendente: atualizado.atendente } : x)));
      }
      setEditandoAtendente(false);
      carregarVencidos(); // atribuicao muda quem "possui" o vencido
    } catch (err) {
      setAtendenteErro(err.response?.data?.message || 'Nao foi possivel salvar o atendente.');
    } finally {
      setAtendenteSalvando(false);
    }
  };

  const fecharDetalhes = () => {
    setDetChamado(null);
    setDetErro('');
    setAcaoMotivo('');
    setAcaoPrioridade('');
    setAcaoErro('');
    setNovoComentario('');
    setEditandoTitulo(false);
    setTituloErro('');
    setEditandoDesc(false);
    setDescErro('');
    setEditandoPrazo(false);
    setPrazoErro('');
    setEditandoAtendente(false);
    setAtendenteErro('');
  };

  const salvarTitulo = async (chamadoId) => {
    const novo = tituloRascunho.trim();
    if (novo.length < 3) {
      setTituloErro('Titulo precisa ter ao menos 3 caracteres.');
      return;
    }
    if (novo.length > 200) {
      setTituloErro('Titulo nao pode ter mais de 200 caracteres.');
      return;
    }
    setTituloSalvando(true);
    setTituloErro('');
    try {
      const res = await axios.patch(`${API}/chamados/${chamadoId}/titulo`, { titulo: novo });
      const atualizado = res.data?.data || null;
      if (atualizado) {
        setDetChamado((prev) => prev ? { ...prev, titulo: atualizado.titulo } : prev);
        setChamados((prev) => prev.map((x) => (x.id === atualizado.id ? { ...x, titulo: atualizado.titulo } : x)));
      }
      setEditandoTitulo(false);
    } catch (err) {
      setTituloErro(err.response?.data?.message || 'Nao foi possivel salvar o titulo.');
    } finally {
      setTituloSalvando(false);
    }
  };

  const salvarDescricao = async (chamadoId) => {
    if (descRascunho.trim().length < 5) {
      setDescErro('Descricao precisa ter ao menos 5 caracteres.');
      return;
    }
    setDescSalvando(true);
    setDescErro('');
    try {
      const res = await axios.patch(`${API}/chamados/${chamadoId}/descricao`, { descricao: descRascunho.trim() });
      const atualizado = res.data?.data || null;
      if (atualizado) {
        setDetChamado((prev) => prev ? { ...prev, descricao: atualizado.descricao } : prev);
        setChamados((prev) => prev.map((x) => (x.id === atualizado.id ? { ...x, ...atualizado } : x)));
      }
      setEditandoDesc(false);
    } catch (err) {
      setDescErro(err.response?.data?.message || 'Nao foi possivel salvar a descricao.');
    } finally {
      setDescSalvando(false);
    }
  };

  const salvarPrazo = async (chamadoId) => {
    setPrazoSalvando(true);
    setPrazoErro('');
    try {
      const body = {
        // vazio = limpar o prazo; datetime-local interpreta no fuso local.
        data_limite: (prazoRascunho || '').trim()
          ? new Date(prazoRascunho).toISOString()
          : null,
      };
      const res = await axios.patch(`${API}/chamados/${chamadoId}/prazo`, body);
      const atualizado = res.data?.data || null;
      if (atualizado) {
        setDetChamado((prev) => (prev ? { ...prev, data_limite: atualizado.data_limite } : prev));
        setChamados((prev) => prev.map((x) => (x.id === atualizado.id ? { ...x, data_limite: atualizado.data_limite } : x)));
      }
      setEditandoPrazo(false);
    } catch (err) {
      setPrazoErro(err.response?.data?.message || 'Nao foi possivel salvar o prazo.');
    } finally {
      setPrazoSalvando(false);
    }
  };

  const alterarStatus = async (chamadoId, novoStatus, motivo, assumir, prioridade) => {
    setAcaoSalvando(true);
    setAcaoErro('');
    try {
      // Checklists marcados como "encerrar junto" vao primeiro: se algum nao
      // puder ser encerrado (ex.: falta informar usado/devolvido), o chamado
      // NAO e fechado e o motivo aparece aqui.
      if (STATUS_QUE_ENCERRAM.includes(novoStatus) && checklistsRef.current) {
        const erroChecklist = await checklistsRef.current.encerrarMarcados();
        if (erroChecklist) {
          setAcaoErro(erroChecklist);
          return;
        }
      }
      const res = await axios.patch(`${API}/chamados/${chamadoId}/status`, {
        status: novoStatus,
        motivo_fechamento: motivo || undefined,
        assumir: assumir || false,
        prioridade: prioridade || undefined,
      });
      const atualizado = res.data?.data || null;
      if (atualizado) {
        setChamados((prev) => prev.map((x) => (x.id === atualizado.id ? { ...x, ...atualizado } : x)));
        setDetChamado(atualizado);
      }
      setAcaoMotivo('');
      setAcaoPrioridade('');
    } catch (err) {
      setAcaoErro(err.response?.data?.message || 'Nao foi possivel alterar o status.');
    } finally {
      setAcaoSalvando(false);
    }
  };

  // Depois de encerrar um checklist pelo detalhe, recarrega o chamado para
  // mostrar o comentario que o checklist acabou de registrar.
  const recarregarDetalhe = async (chamadoId) => {
    try {
      const res = await axios.get(`${API}/chamados/${chamadoId}`);
      if (res.data?.data) setDetChamado(res.data.data);
    } catch (_) { /* o detalhe ja aberto continua valendo */ }
  };

  const salvarComentario = async (chamadoId) => {
    if (!novoComentario.trim()) return;
    setComentarioSalvando(true);
    setAcaoErro('');
    try {
      const res = await axios.post(`${API}/chamados/${chamadoId}/comentarios`, { texto: novoComentario.trim() });
      const comentarios = res.data?.data || [];
      setDetChamado((prev) => (prev ? { ...prev, comentarios } : prev));
      setNovoComentario('');
    } catch (err) {
      setAcaoErro(err.response?.data?.message || 'Nao foi possivel adicionar o comentario.');
    } finally {
      setComentarioSalvando(false);
    }
  };

  const carregarChamados = () => {
    setLoading(true);
    return axios.get(`${API}/chamados`)
      .then((res) => setChamados(res.data?.data || []))
      .catch(() => setErro('Nao foi possivel carregar os chamados.'))
      .finally(() => setLoading(false));
  };

  const abrirNovo = async () => {
    // Pre-seleciona a filial do proprio usuario quando ele tiver uma cadastrada;
    // o atendente pode trocar pra outra do catalogo se estiver abrindo em nome
    // de alguem de outra unidade.
    const filialPadrao = user?.filial || '';
    setNovoForm({
      titulo: '', descricao: '', categoria: '', prioridade: 'urgente',
      filial: filialPadrao, solicitante: '', contato_id: null, data_limite: '',
    });
    setNovoErro('');
    setNovoComChecklist(false);
    setNovoAberto(true);

    const promises = [];
    if (podeInformarSolicitante && !novoContatos.length) {
      promises.push(
        axios.get(`${API}/contatos`)
          .then((r) => setNovoContatos(r.data?.data || []))
          .catch(() => { /* picker de contatos e opcional, nao bloqueia */ }),
      );
    }
    if (!novoCategorias.length) {
      promises.push(
        axios.get(`${API}/chamados/categorias-ativas`)
          .then((r) => {
            const list = r.data?.data || [];
            setNovoCategorias(list);
            setNovoForm((f) => ({ ...f, categoria: f.categoria || list[0]?.chave || '' }));
          })
          .catch(() => setNovoErro('Nao foi possivel carregar as categorias.')),
      );
    } else {
      setNovoForm((f) => ({ ...f, categoria: f.categoria || novoCategorias[0]?.chave || '' }));
    }
    if (!novoFiliais.length) {
      promises.push(
        axios.get(`${API}/chamados/filiais-ativas`)
          .then((r) => setNovoFiliais(r.data?.data || []))
          .catch(() => { /* lista de filiais nao bloqueia o cadastro */ }),
      );
    }
    await Promise.all(promises);
  };

  const salvarNovo = async () => {
    if ((novoForm.titulo || '').trim().length < 3) {
      setNovoErro('Titulo precisa ter ao menos 3 caracteres.');
      return;
    }
    if ((novoForm.descricao || '').trim().length < 5) {
      setNovoErro('Descricao precisa ter ao menos 5 caracteres.');
      return;
    }
    setNovoSalvando(true);
    setNovoErro('');
    try {
      const criado = await axios.post(`${API}/chamados`, {
        titulo: novoForm.titulo.trim(),
        descricao: novoForm.descricao.trim(),
        categoria: novoForm.categoria || undefined,
        prioridade: novoForm.prioridade || 'urgente',
        solicitante_filial: (novoForm.filial || '').trim() || undefined,
        // Solicitante informado pela equipe (contato escolhido ou texto livre).
        solicitante: podeInformarSolicitante ? ((novoForm.solicitante || '').trim() || undefined) : undefined,
        contato_id: podeInformarSolicitante ? (novoForm.contato_id || undefined) : undefined,
        // datetime-local vem sem timezone; o Date interpreta no fuso local.
        data_limite: (novoForm.data_limite || '').trim()
          ? new Date(novoForm.data_limite).toISOString()
          : undefined,
      });
      setNovoAberto(false);
      // Abrir o checklist junto: vai direto para a aba Insumos com um checklist
      // novo ja vinculado ao chamado recem-criado.
      const novoId = criado.data?.data?.id;
      if (novoComChecklist && novoId) {
        navigate(`/ti/insumos?novo=1&chamado=${novoId}`);
        return;
      }
      await carregarChamados();
    } catch (err) {
      setNovoErro(err.response?.data?.message || 'Nao foi possivel abrir o chamado.');
    } finally {
      setNovoSalvando(false);
    }
  };

  const fecharHistorico = () => {
    setHistChamado(null);
    setHistMensagens([]);
    setHistErro('');
  };

  return (
    <div className="content-shell">
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">TI</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ marginBottom: 0 }}>Chamados</h1>
            <InfoFluxoAtendimento />
          </div>
          <p>
            {temPermissao('ti:chamados')
              ? 'Acompanhe e atenda as solicitacoes registradas.'
              : 'Abra chamados para a equipe de TI e acompanhe o andamento dos seus.'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {podeVerVencidos && vencidosIds.size > 0 && (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => setSoVencidos((v) => !v)}
              title={soVencidos
                ? 'Mostrando so os vencidos. Clique para ver todos.'
                : 'Seus chamados vencidos (atribuidos a voce + sem atendente). Clique para filtrar.'}
              style={soVencidos
                ? { background: '#dc3545', color: '#fff', border: 'none' }
                : { background: 'rgba(220,53,69,0.12)', color: '#dc3545', border: '1px solid rgba(220,53,69,0.45)' }}
            >
              <i className="bi bi-exclamation-triangle-fill me-1" />
              {vencidosIds.size} {vencidosIds.size === 1 ? 'vencido' : 'vencidos'}
            </button>
          )}
          {temPermissao('ti:chamados') && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setGerenciarAtendentes(true)}
              title="Cadastrar ou remover atendentes"
            >
              <i className="bi bi-headset me-1" /> Atendentes
            </button>
          )}
          {podeAbrir && (
            <button
              type="button"
              className="btn btn-sm"
              onClick={abrirNovo}
              style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
            >
              <i className="bi bi-plus-lg me-1" /> Novo chamado
            </button>
          )}
        </div>
      </section>

      <div className="filter-card">
        <div className="filter-card__field">
          <select value={statusFiltro} onChange={(e) => setStatusFiltro(e.target.value)}>
            <option value={FILTRO_EM_ABERTO}>Em aberto</option>
            <option value="">Todos status</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="filter-card__field">
          <select value={prioridadeFiltro} onChange={(e) => setPrioridadeFiltro(e.target.value)}>
            <option value="">Todas prioridades</option>
            {Object.entries(PRIORIDADE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="filter-card__field">
          <select value={nivelFiltro} onChange={(e) => setNivelFiltro(e.target.value)}>
            <option value="">Todos niveis</option>
            {NIVEIS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      {erro ? <div className="feedback-banner feedback-banner--error">{erro}</div> : null}

      <section className="content-card">
        {loading ? (
          <div className="content-card--centered">
            <div className="screen-loader__spinner" />
            <p>Carregando chamados...</p>
          </div>
        ) : chamadosFiltrados.length ? (
          <div className="table-shell">
            <table className="portal-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Titulo</th>
                  <th>Categoria</th>
                  <th>Prioridade</th>
                  <th>Nivel</th>
                  <th title="Semaforo de SLA: 1a resposta | Solucao">SLA</th>
                  <th>Status</th>
                  <th style={{ maxWidth: 110 }}>Solicitante</th>
                  <th>Atendente</th>
                  <th>Aberto em</th>
                  <th>Prazo</th>
                  <th>Fechado em</th>
                  <th style={{ textAlign: 'right' }}>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {chamadosFiltrados.map((c) => {
                  const vencido = isVencido(c);
                  return (
                  <tr
                    key={c.id}
                    style={vencido ? { background: 'rgba(220, 53, 69, 0.08)' } : undefined}
                  >
                    <td className="cell-muted">
                      {vencido && (
                        <i
                          className="bi bi-exclamation-triangle-fill"
                          title="Chamado vencido (prazo de entrega expirado)"
                          style={{ color: '#dc3545', marginRight: 6 }}
                        />
                      )}
                      {c.id}
                    </td>
                    <td><span className="cell-strong">{c.titulo}</span></td>
                    <td className="cell-muted">{c.categoria}</td>
                    <td>
                      <SeletorPrioridade
                        chamado={c}
                        onSaved={(novo) => novo && setChamados((prev) =>
                          prev.map((x) => (x.id === novo.id ? { ...x, ...novo } : x))
                        )}
                      />
                    </td>
                    <td>
                      <SeletorNivel
                        chamado={c}
                        onSaved={(novo) => novo && setChamados((prev) =>
                          prev.map((x) => (x.id === novo.id ? { ...x, nivel: novo.nivel } : x))
                        )}
                      />
                    </td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <SlaDot info={c.sla?.ttfr} kind="ttfr" />
                        <SlaDot info={c.sla?.ttr}  kind="ttr" />
                      </span>
                    </td>
                    <td>
                      <span className="permissao-chip">{STATUS_LABEL[c.status] || c.status}</span>
                    </td>
                    <td
                      className="cell-muted"
                      title={c.solicitante || ''}
                      style={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {c.solicitante || '-'}
                    </td>
                    <td className="cell-muted">
                      {temPermissao('ti:chamados') ? (
                        <SeletorAtendente
                          chamado={c}
                          atendentes={atendentes}
                          onSaved={(novo) => {
                            if (!novo) return;
                            setChamados((prev) => prev.map((x) => (x.id === novo.id ? { ...x, atendente: novo.atendente } : x)));
                            carregarVencidos(); // atribuicao muda quem "possui" o vencido
                          }}
                        />
                      ) : (c.atendente || '-')}
                    </td>
                    <td className="cell-muted">{formatDateBR(c.created_at)}</td>
                    <td style={{ color: vencido ? '#dc3545' : 'var(--portal-muted)', fontWeight: vencido ? 600 : 400 }}>
                      {c.data_limite ? formatDataHoraBR(c.data_limite) : '-'}
                    </td>
                    <td className="cell-muted">{c.fechado_em ? formatDateBR(c.fechado_em) : '-'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => abrirDetalhes(c)}
                          title="Ver descricao, motivo de fechamento e comentarios"
                        >
                          <i className="bi bi-info-circle" /> Detalhes
                        </button>
                        {c.contato_id ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-info"
                            onClick={() => abrirHistorico(c)}
                            title="Ver historico da conversa WhatsApp"
                          >
                            <i className="bi bi-chat-left-text" /> Historico
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <strong>Nenhum chamado registrado</strong>
            <span>Quando um chamado for aberto, ele aparecera aqui.</span>
          </div>
        )}
      </section>

      {gerenciarAtendentes && (
        <GerenciarAtendentes
          onClose={() => setGerenciarAtendentes(false)}
          onChanged={carregarAtendentes}
        />
      )}

      {detChamado && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(8, 23, 43, 0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) fecharDetalhes(); }}
        >
          <div
            className="content-card"
            style={{
              width: 'min(680px, 96vw)', maxHeight: '90vh',
              display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden',
            }}
          >
            {/* ── Header ── */}
            <header style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--portal-border)',
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--portal-muted)', letterSpacing: '0.05em' }}>
                    CHAMADO #{detChamado.id}
                  </span>
                  {/* Badge status */}
                  <span className="permissao-chip" style={{ fontSize: '0.72rem' }}>
                    {STATUS_LABEL[detChamado.status] || detChamado.status}
                  </span>
                  {/* Badge prioridade colorido */}
                  {detChamado.prioridade && (() => {
                    const cor = PRIORIDADE_CORES[detChamado.prioridade] || '#5E7A82';
                    return (
                      <span style={{
                        background: `${cor}22`, color: cor, border: `1px solid ${cor}55`,
                        borderRadius: 8, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600,
                      }}>
                        {PRIORIDADE_LABEL[detChamado.prioridade] || detChamado.prioridade}
                      </span>
                    );
                  })()}
                  {/* Nivel editavel: o retorno ja traz o comentario da troca */}
                  <SeletorNivel
                    chamado={detChamado}
                    onSaved={(novo) => {
                      if (!novo) return;
                      setDetChamado(novo);
                      setChamados((prev) => prev.map((x) => (x.id === novo.id ? { ...x, nivel: novo.nivel } : x)));
                    }}
                  />
                </div>
                {editandoTitulo ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <input
                      type="text"
                      className="form-control"
                      value={tituloRascunho}
                      onChange={(e) => { setTituloRascunho(e.target.value); setTituloErro(''); }}
                      disabled={tituloSalvando}
                      maxLength={200}
                      style={{ fontSize: '0.95rem', fontWeight: 600 }}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); salvarTitulo(detChamado.id); }
                        if (e.key === 'Escape') { setEditandoTitulo(false); setTituloErro(''); }
                      }}
                    />
                    {tituloErro && (
                      <div className="feedback-banner feedback-banner--error" style={{ fontSize: '0.82rem' }}>
                        {tituloErro}
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={tituloSalvando}
                        onClick={() => { setEditandoTitulo(false); setTituloErro(''); }}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={tituloSalvando}
                        onClick={() => salvarTitulo(detChamado.id)}
                        style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
                      >
                        <i className="bi bi-check2 me-1" />
                        {tituloSalvando ? 'Salvando...' : 'Salvar'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--portal-text)', lineHeight: 1.35, flex: 1, minWidth: 0 }}>
                      {detChamado.titulo}
                    </div>
                    {temPermissao('ti:chamados') && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        style={{ fontSize: '0.72rem', padding: '2px 8px', flexShrink: 0 }}
                        onClick={() => { setTituloRascunho(detChamado.titulo || ''); setTituloErro(''); setEditandoTitulo(true); }}
                        title="Editar titulo"
                      >
                        <i className="bi bi-pencil" />
                      </button>
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={fecharDetalhes}
                aria-label="Fechar"
                style={{ flexShrink: 0 }}
              >
                <i className="bi bi-x-lg" />
              </button>
            </header>

            {/* ── Body ── */}
            <div style={{
              flex: 1, overflowY: 'auto', padding: '16px 20px',
              display: 'flex', flexDirection: 'column', gap: 16,
            }}>
              {detLoading && (
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <div className="screen-loader__spinner" />
                </div>
              )}
              {detErro && <div className="feedback-banner feedback-banner--error">{detErro}</div>}

              {/* Grade de metadados */}
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px',
                padding: 14, borderRadius: 10,
                border: '1px solid var(--portal-border)',
                background: 'var(--portal-surface-soft)',
              }}>
                {[
                  { label: 'Solicitante', valor: detChamado.solicitante, icon: 'bi-person' },
                  { label: 'Categoria',   valor: detChamado.categoria,   icon: 'bi-tag' },
                  { label: 'Filial',      valor: detChamado.solicitante_filial, icon: 'bi-building' },
                  { label: 'Aberto em',   valor: formatDataHoraBR(detChamado.created_at), icon: 'bi-calendar-event' },
                  { label: 'Fechado em',  valor: detChamado.fechado_em ? formatDataHoraBR(detChamado.fechado_em) : null, icon: 'bi-calendar-check' },
                ].map(({ label, valor, icon, cor }) => (
                  <div key={label}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--portal-muted)', marginBottom: 2 }}>
                      <i className={`bi ${icon} me-1`} />{label}
                    </div>
                    <div style={{ fontSize: '0.83rem', fontWeight: cor ? 600 : 400, color: cor || (valor ? 'var(--portal-text)' : 'var(--portal-muted)') }}>
                      {valor || '—'}{cor ? ' (vencido)' : ''}
                    </div>
                  </div>
                ))}
              </div>

              {/* Atendente responsavel — atribuir SEM iniciar o chamado */}
              {(
                <div style={{
                  padding: '10px 14px', borderRadius: 10,
                  border: '1px solid var(--portal-border)',
                  background: 'var(--portal-surface-soft)',
                  display: 'flex', flexDirection: 'column', gap: 8,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--portal-muted)' }}>
                      <i className="bi bi-headset me-1" />Atendente responsavel
                    </div>
                    {temPermissao('ti:chamados') && !editandoAtendente && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        style={{ fontSize: '0.72rem', padding: '2px 8px' }}
                        onClick={() => {
                          setAtendenteRascunho(detChamado.atendente || '');
                          setAtendenteErro('');
                          setEditandoAtendente(true);
                        }}
                      >
                        <i className="bi bi-person-gear me-1" />{detChamado.atendente ? 'Alterar' : 'Atribuir'}
                      </button>
                    )}
                  </div>
                  {editandoAtendente ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <select
                        className="form-select"
                        value={atendenteRascunho}
                        onChange={(e) => { setAtendenteRascunho(e.target.value); setAtendenteErro(''); }}
                        disabled={atendenteSalvando}
                        style={{ fontSize: '0.85rem' }}
                      >
                        <option value="">(sem atendente)</option>
                        {atendentes.map((a) => (
                          <option key={a.nome} value={a.nome}>{a.nome}</option>
                        ))}
                        {/* mantem o atual mesmo se nao estiver na lista de vinculados */}
                        {detChamado.atendente && !atendentes.some((a) => a.nome === detChamado.atendente) && (
                          <option value={detChamado.atendente}>{detChamado.atendente}</option>
                        )}
                      </select>
                      {atendenteErro && (
                        <div className="feedback-banner feedback-banner--error" style={{ fontSize: '0.82rem' }}>
                          {atendenteErro}
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          disabled={atendenteSalvando}
                          onClick={() => { setEditandoAtendente(false); setAtendenteErro(''); }}
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={atendenteSalvando}
                          onClick={() => salvarAtendente(detChamado.id)}
                          style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
                        >
                          <i className="bi bi-check2 me-1" />
                          {atendenteSalvando ? 'Salvando...' : 'Salvar'}
                        </button>
                      </div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--portal-muted)' }}>
                        Atribuir aqui nao inicia o chamado — o status nao muda. Serve pra
                        definir quem recebe os alertas de vencimento.
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: '0.85rem', color: detChamado.atendente ? 'var(--portal-text)' : 'var(--portal-muted)' }}>
                      {detChamado.atendente || 'Sem atendente'}
                    </div>
                  )}
                </div>
              )}

              {/* Prazo de entrega — visivel a todos; edicao apenas pra equipe */}
              {(
                <div style={{
                  padding: '10px 14px', borderRadius: 10,
                  border: '1px solid var(--portal-border)',
                  background: 'var(--portal-surface-soft)',
                  display: 'flex', flexDirection: 'column', gap: 8,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--portal-muted)' }}>
                      <i className="bi bi-alarm me-1" />Prazo de entrega
                    </div>
                    {temPermissao('ti:chamados') && !editandoPrazo && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        style={{ fontSize: '0.72rem', padding: '2px 8px' }}
                        onClick={() => {
                          setPrazoRascunho(paraDatetimeLocal(detChamado.data_limite));
                          setPrazoErro('');
                          setEditandoPrazo(true);
                        }}
                      >
                        <i className="bi bi-pencil me-1" />{detChamado.data_limite ? 'Alterar' : 'Definir'}
                      </button>
                    )}
                  </div>
                  {editandoPrazo ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={prazoRascunho}
                        onChange={(e) => { setPrazoRascunho(e.target.value); setPrazoErro(''); }}
                        disabled={prazoSalvando}
                        style={{ fontSize: '0.85rem' }}
                      />
                      {prazoErro && (
                        <div className="feedback-banner feedback-banner--error" style={{ fontSize: '0.82rem' }}>
                          {prazoErro}
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          disabled={prazoSalvando || !detChamado.data_limite}
                          onClick={() => { setPrazoRascunho(''); salvarPrazo(detChamado.id); }}
                          title="Remover o prazo de entrega"
                        >
                          <i className="bi bi-x-circle me-1" />Limpar
                        </button>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={prazoSalvando}
                            onClick={() => { setEditandoPrazo(false); setPrazoErro(''); }}
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm"
                            disabled={prazoSalvando}
                            onClick={() => salvarPrazo(detChamado.id)}
                            style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
                          >
                            <i className="bi bi-check2 me-1" />
                            {prazoSalvando ? 'Salvando...' : 'Salvar'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: '0.85rem', fontWeight: isVencido(detChamado) ? 600 : 400, color: isVencido(detChamado) ? '#dc3545' : (detChamado.data_limite ? 'var(--portal-text)' : 'var(--portal-muted)') }}>
                      {detChamado.data_limite ? formatDataHoraBR(detChamado.data_limite) : 'Sem prazo definido'}
                      {isVencido(detChamado) ? ' (vencido)' : ''}
                    </div>
                  )}
                </div>
              )}

              {/* Conversa de origem (chamados nascidos de um atendimento WhatsApp) */}
              {detChamado.conversa_id && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-info"
                  onClick={() => navigate(
                    `/ti/atendimentos?conversa=${detChamado.conversa_id}`
                    + `&marco=${encodeURIComponent(detChamado.created_at || '')}`,
                  )}
                  style={{ alignSelf: 'flex-start' }}
                  title="Abrir a conversa que originou este chamado, no momento em que ele foi aberto"
                >
                  <i className="bi bi-chat-left-text me-1" /> Abrir conversa de origem
                </button>
              )}

              {/* Descricao */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--portal-muted)' }}>
                    <i className="bi bi-file-text me-1" />Descricao
                  </div>
                  {temPermissao('ti:chamados') && !editandoDesc && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      style={{ fontSize: '0.72rem', padding: '2px 8px' }}
                      onClick={() => { setDescRascunho(detChamado.descricao || ''); setDescErro(''); setEditandoDesc(true); }}
                    >
                      <i className="bi bi-pencil me-1" />Editar
                    </button>
                  )}
                </div>
                {editandoDesc ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <textarea
                      className="form-control"
                      rows={5}
                      value={descRascunho}
                      onChange={(e) => { setDescRascunho(e.target.value); setDescErro(''); }}
                      disabled={descSalvando}
                      style={{ fontSize: '0.85rem', lineHeight: 1.55 }}
                      autoFocus
                    />
                    {descErro && (
                      <div className="feedback-banner feedback-banner--error" style={{ fontSize: '0.82rem' }}>
                        {descErro}
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={descSalvando}
                        onClick={() => { setEditandoDesc(false); setDescErro(''); }}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={descSalvando}
                        onClick={() => salvarDescricao(detChamado.id)}
                        style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
                      >
                        <i className="bi bi-check2 me-1" />
                        {descSalvando ? 'Salvando...' : 'Salvar'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    padding: '10px 14px', borderRadius: 8,
                    border: '1px solid var(--portal-border)',
                    background: 'var(--portal-surface-soft)',
                    whiteSpace: 'pre-wrap', fontSize: '0.85rem', lineHeight: 1.55,
                  }}>
                    {detChamado.descricao || <span className="cell-muted">(sem descricao)</span>}
                  </div>
                )}
              </div>

              {/* Motivo de fechamento */}
              {detChamado.motivo_fechamento && (
                <div style={{
                  padding: '10px 14px', borderRadius: 8,
                  border: '1px solid rgba(76, 181, 83, 0.45)',
                  background: 'rgba(76, 181, 83, 0.08)',
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                }}>
                  <i className="bi bi-flag-fill" style={{ color: '#4CB553', marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#4CB553', marginBottom: 2 }}>
                      Motivo de fechamento
                    </div>
                    <div style={{ fontSize: '0.85rem' }}>{detChamado.motivo_fechamento}</div>
                  </div>
                </div>
              )}

              {/* Checklist de insumos vinculado ao chamado */}
              {podeInsumos && !detLoading && detChamado.id ? (
                <ChecklistsDoChamado
                  key={detChamado.id}
                  ref={checklistsRef}
                  chamado={detChamado}
                  chamadoAberto={STATUS_ABERTOS_SET.has(detChamado.status)}
                  onMudou={() => recarregarDetalhe(detChamado.id)}
                />
              ) : null}

              {/* Acoes de status */}
              {temPermissao('ti:chamados') && STATUS_ABERTOS_SET.has(detChamado.status) && (
                <div style={{
                  padding: '12px 14px', borderRadius: 10,
                  border: '1px solid var(--portal-border)',
                  background: 'var(--portal-surface-soft)',
                  display: 'flex', flexDirection: 'column', gap: 10,
                }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--portal-muted)' }}>
                    <i className="bi bi-lightning me-1" />Acoes
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 4 }}>
                      Motivo <span style={{ fontStyle: 'italic' }}>(obrigatorio ao resolver, fechar ou cancelar)</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={acaoMotivo}
                      onChange={(e) => { setAcaoMotivo(e.target.value); setAcaoErro(''); }}
                      placeholder="Descreva a resolucao ou motivo de encerramento..."
                      disabled={acaoSalvando}
                      style={{ fontSize: '0.85rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 4 }}>
                      Ajustar prioridade <span style={{ fontStyle: 'italic' }}>(opcional — util pra reclassificar chamados receptivos no encerramento)</span>
                    </label>
                    <select
                      className="form-control"
                      value={acaoPrioridade}
                      onChange={(e) => { setAcaoPrioridade(e.target.value); setAcaoErro(''); }}
                      disabled={acaoSalvando}
                      style={{ fontSize: '0.85rem', maxWidth: 220 }}
                    >
                      <option value="">Manter atual ({PRIORIDADE_LABEL[detChamado.prioridade] || detChamado.prioridade})</option>
                      {Object.entries(PRIORIDADE_LABEL).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </div>
                  {acaoErro && (
                    <div className="feedback-banner feedback-banner--error" style={{ fontSize: '0.82rem' }}>
                      {acaoErro}
                    </div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {(BOTOES_POR_STATUS[detChamado.status] || []).map(({ status: novoStatus, label, cor, exigeMotivo, assumir }) => (
                      <button
                        key={novoStatus}
                        type="button"
                        className="btn btn-sm"
                        disabled={acaoSalvando}
                        onClick={() => {
                          if (exigeMotivo && !acaoMotivo.trim()) {
                            setAcaoErro(`Informe o motivo antes de "${label.toLowerCase()}".`);
                            return;
                          }
                          alterarStatus(detChamado.id, novoStatus, acaoMotivo, assumir, acaoPrioridade);
                        }}
                        style={{ background: cor, color: '#fff', border: 'none', opacity: acaoSalvando ? 0.7 : 1 }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Reabrir chamado encerrado */}
              {temPermissao('ti:chamados') && STATUS_QUE_ENCERRAM.includes(detChamado.status) && (
                <div style={{
                  padding: '12px 14px', borderRadius: 10,
                  border: '1px solid var(--portal-border)',
                  background: 'var(--portal-surface-soft)',
                  display: 'flex', flexDirection: 'column', gap: 10,
                }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--portal-muted)' }}>
                    <i className="bi bi-arrow-counterclockwise me-1" />Reabrir chamado
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--portal-muted)' }}>
                    Status atual: <strong>{STATUS_LABEL[detChamado.status] || detChamado.status}</strong>. A reabertura volta o chamado para <strong>Aberto</strong>, limpa a data de fechamento e registra o motivo nos comentarios.
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 4 }}>
                      Motivo da reabertura <span style={{ fontStyle: 'italic' }}>(obrigatorio)</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={acaoMotivo}
                      onChange={(e) => { setAcaoMotivo(e.target.value); setAcaoErro(''); }}
                      placeholder="Explique por que o chamado precisa ser reaberto..."
                      disabled={acaoSalvando}
                      style={{ fontSize: '0.85rem' }}
                    />
                  </div>
                  {acaoErro && (
                    <div className="feedback-banner feedback-banner--error" style={{ fontSize: '0.82rem' }}>
                      {acaoErro}
                    </div>
                  )}
                  <div>
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={acaoSalvando}
                      onClick={() => {
                        if (!acaoMotivo.trim()) {
                          setAcaoErro('Informe o motivo antes de reabrir.');
                          return;
                        }
                        alterarStatus(detChamado.id, 'aberto', acaoMotivo, false, undefined);
                      }}
                      style={{ background: '#2A8A9C', color: '#fff', border: 'none', opacity: acaoSalvando ? 0.7 : 1 }}
                    >
                      <i className="bi bi-arrow-counterclockwise me-1" />
                      Reabrir chamado
                    </button>
                  </div>
                </div>
              )}

              {/* Comentarios */}
              <div>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--portal-muted)', marginBottom: 8 }}>
                  <i className="bi bi-chat-square-text me-1" />
                  Comentarios{detChamado.comentarios?.length ? ` · ${detChamado.comentarios.length}` : ''}
                </div>
                {!detChamado.comentarios?.length ? (
                  <div className="cell-muted" style={{ fontSize: '0.82rem', paddingLeft: 4 }}>
                    Nenhum comentario registrado.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {detChamado.comentarios.map((com) => (
                      <div
                        key={com.id}
                        style={{
                          padding: '8px 12px', borderRadius: 8,
                          border: '1px solid var(--portal-border)',
                          background: 'var(--portal-surface)',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--portal-text)' }}>
                            <i className="bi bi-person-circle me-1" style={{ color: 'var(--portal-muted)' }} />
                            {com.autor || '(sem autor)'}
                          </span>
                          <span style={{ fontSize: '0.7rem', color: 'var(--portal-muted)' }}>
                            {formatDataHoraBR(com.created_at)}
                          </span>
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', lineHeight: 1.5 }}>
                          {com.texto}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {temPermissao('ti:chamados') && (
                  <div style={{ marginTop: 10 }}>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={novoComentario}
                      onChange={(e) => setNovoComentario(e.target.value)}
                      placeholder="Adicionar comentario interno..."
                      disabled={comentarioSalvando}
                      style={{ fontSize: '0.85rem' }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={comentarioSalvando || !novoComentario.trim()}
                        onClick={() => salvarComentario(detChamado.id)}
                      >
                        <i className="bi bi-send me-1" />
                        {comentarioSalvando ? 'Salvando...' : 'Comentar'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {histChamado && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(8, 23, 43, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            className="content-card"
            style={{
              width: 'min(640px, 96vw)',
              maxHeight: '88vh',
              display: 'flex',
              flexDirection: 'column',
              padding: 0,
              overflow: 'hidden',
            }}
          >
            <header
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '14px 18px',
                borderBottom: '1px solid var(--portal-border)',
                gap: 12,
              }}
            >
              <div>
                <strong>Historico do chamado #{histChamado.id}</strong>
                <div className="cell-muted" style={{ fontSize: '0.78rem', marginTop: 2 }}>
                  {histChamado.titulo}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={fecharHistorico}
                aria-label="Fechar"
              >
                <i className="bi bi-x-lg" />
              </button>
            </header>

            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: 16,
                background: 'var(--portal-bg)',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              {histLoading ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <div className="screen-loader__spinner" />
                  <p className="cell-muted" style={{ marginTop: 8 }}>Carregando mensagens...</p>
                </div>
              ) : histErro ? (
                <div className="feedback-banner feedback-banner--error">{histErro}</div>
              ) : !histMensagens.length ? (
                <div className="empty-state" style={{ background: 'transparent' }}>
                  <strong>Nenhuma mensagem encontrada</strong>
                  <span>Este chamado nao tem conversa WhatsApp vinculada nesse periodo.</span>
                </div>
              ) : (
                histMensagens.map((m) => {
                  const isIn = m.direcao === 'in';
                  const isMidia = m.tipo && m.tipo !== 'texto';
                  // Bolhas: cliente (in) com surface-soft do tema; bot/atendente
                  // (out) com tom do brand (#2A8A9C) translucido sobre o fundo.
                  const bg = isIn
                    ? 'var(--portal-surface-soft)'
                    : 'rgba(42, 138, 156, 0.22)';
                  const border = isIn
                    ? 'var(--portal-border)'
                    : 'rgba(42, 138, 156, 0.55)';
                  return (
                    <div
                      key={m.id}
                      style={{
                        display: 'flex',
                        justifyContent: isIn ? 'flex-start' : 'flex-end',
                      }}
                    >
                      <div
                        style={{
                          maxWidth: '76%',
                          padding: '8px 12px',
                          borderRadius: 12,
                          background: bg,
                          border: `1px solid ${border}`,
                          color: 'var(--portal-text)',
                          fontSize: '0.85rem',
                          lineHeight: 1.4,
                        }}
                      >
                        <div
                          style={{
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            color: 'var(--portal-muted)',
                            marginBottom: 4,
                          }}
                        >
                          {autorMensagem(m)}
                          {m.chamado_id ? (
                            <span style={{ marginLeft: 6 }}>
                              · #{m.chamado_id}
                            </span>
                          ) : null}
                        </div>
                        {isMidia && m.midia_signed_url ? (
                          m.tipo === 'imagem' ? (
                            <img
                              src={m.midia_signed_url}
                              alt="anexo"
                              style={{ maxWidth: '100%', borderRadius: 6, marginTop: 4 }}
                            />
                          ) : m.tipo === 'figurinha' ? (
                            <img
                              src={m.midia_signed_url}
                              alt="figurinha"
                              style={{ maxWidth: 120, marginTop: 4 }}
                            />
                          ) : (
                            <a
                              href={m.midia_signed_url}
                              target="_blank"
                              rel="noreferrer"
                              style={{ display: 'inline-block', marginTop: 4, color: '#2A8A9C' }}
                            >
                              <i className="bi bi-paperclip" /> {m.tipo} ({m.midia_mime})
                            </a>
                          )
                        ) : null}
                        {m.conteudo ? (
                          <div style={{ whiteSpace: 'pre-wrap', color: 'var(--portal-text)' }}>
                            {m.conteudo}
                          </div>
                        ) : isMidia ? (
                          <div style={{ fontStyle: 'italic', color: 'var(--portal-muted)' }}>
                            [{m.tipo}]
                          </div>
                        ) : null}
                        <div
                          style={{
                            fontSize: '0.65rem',
                            color: 'var(--portal-muted)',
                            marginTop: 4,
                            textAlign: 'right',
                          }}
                        >
                          {formatDataHoraBR(m.criada_em)}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <footer
              style={{
                padding: '10px 18px',
                borderTop: '1px solid var(--portal-border)',
                fontSize: '0.78rem',
                color: 'var(--portal-muted)',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <strong style={{ color: 'var(--portal-text)' }}>
                {histMensagens.length} mensagem(ns)
              </strong>
              <span style={{ fontSize: '0.72rem', lineHeight: 1.4 }}>
                Inclui mensagens vinculadas ao chamado <strong>#{histChamado.id}</strong> e
                interacoes da conversa entre o chamado anterior do contato e este (intake,
                menu de retorno, notificacoes do bot).
              </span>
            </footer>
          </div>
        </div>
      )}

      {novoAberto && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(8, 23, 43, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            className="content-card"
            style={{
              width: 'min(560px, 96vw)',
              maxHeight: '90vh',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <header
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '14px 18px',
                borderBottom: '1px solid var(--portal-border)',
                gap: 12,
              }}
            >
              <strong>
                <i className="bi bi-plus-circle me-2" /> Novo chamado
              </strong>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setNovoAberto(false)}
                disabled={novoSalvando}
                aria-label="Fechar"
              >
                <i className="bi bi-x-lg" />
              </button>
            </header>

            <div style={{ padding: 18, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {novoErro ? (
                <div className="feedback-banner feedback-banner--error" style={{ fontSize: '0.85rem' }}>
                  {novoErro}
                </div>
              ) : null}

              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Titulo</label>
                <input
                  type="text"
                  className="form-control"
                  value={novoForm.titulo}
                  onChange={(e) => setNovoForm({ ...novoForm, titulo: e.target.value })}
                  placeholder='Ex: Impressora da recepcao nao imprime'
                  maxLength={200}
                  autoFocus
                />
              </div>

              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Descricao</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={novoForm.descricao}
                  onChange={(e) => setNovoForm({ ...novoForm, descricao: e.target.value })}
                  placeholder='O que aconteceu? Quando comecou? Mensagens de erro?'
                />
              </div>

              <div className="row g-3" style={{ margin: 0 }}>
                <div className="col-12 col-sm-6" style={{ paddingLeft: 0 }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Categoria</label>
                  <select
                    className="form-select"
                    value={novoForm.categoria}
                    onChange={(e) => setNovoForm({ ...novoForm, categoria: e.target.value })}
                  >
                    {novoCategorias.length === 0 ? (
                      <option value="">Carregando...</option>
                    ) : novoCategorias.map((c) => (
                      <option key={c.chave} value={c.chave}>{c.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-sm-6" style={{ paddingRight: 0 }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Prioridade</label>
                  <select
                    className="form-select"
                    value={novoForm.prioridade}
                    onChange={(e) => setNovoForm({ ...novoForm, prioridade: e.target.value })}
                  >
                    <option value="baixa">Baixa</option>
                    <option value="media">Media</option>
                    <option value="alta">Alta</option>
                    <option value="urgente">Urgente</option>
                  </select>
                </div>
              </div>

              {podeInformarSolicitante && (
                <div>
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                    Solicitante <span style={{ fontStyle: 'italic' }}>(opcional)</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    list="chamado-contatos-datalist"
                    value={novoForm.solicitante}
                    onChange={(e) => {
                      const val = e.target.value;
                      const match = novoContatos.find(
                        (ct) => (ct.push_name && ct.push_name === val) || (ct.numero && ct.numero === val),
                      );
                      setNovoForm((f) => ({ ...f, solicitante: val, contato_id: match?.id || null }));
                    }}
                    placeholder="Escolha um contato ou digite um nome"
                    maxLength={150}
                  />
                  <datalist id="chamado-contatos-datalist">
                    {novoContatos.map((ct) => (
                      <option key={ct.id} value={ct.push_name || ct.numero || ''}>
                        {ct.numero ? `${ct.numero}${ct.filial ? ` - ${ct.filial}` : ''}` : ''}
                      </option>
                    ))}
                  </datalist>
                  <div style={{ fontSize: '0.7rem', color: 'var(--portal-muted)', marginTop: 2 }}>
                    {novoForm.contato_id
                      ? 'Contato do WhatsApp vinculado.'
                      : 'Em branco = aberto em seu nome. Texto livre tambem e aceito.'}
                  </div>
                </div>
              )}

              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                  Data limite de entrega <span style={{ fontStyle: 'italic' }}>(opcional)</span>
                </label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={novoForm.data_limite}
                  onChange={(e) => setNovoForm({ ...novoForm, data_limite: e.target.value })}
                />
              </div>

              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Filial</label>
                <select
                  className="form-select"
                  value={novoForm.filial}
                  onChange={(e) => setNovoForm({ ...novoForm, filial: e.target.value })}
                >
                  <option value="">(usar a do meu cadastro)</option>
                  {novoFiliais.map((f) => (
                    <option key={f.id || f.nome} value={f.nome}>
                      {f.codigo ? `${f.codigo} - ${f.nome}` : f.nome}
                    </option>
                  ))}
                </select>
              </div>

              {podeInsumos ? (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: '0.85rem' }}>
                  <input
                    type="checkbox"
                    checked={novoComChecklist}
                    onChange={(e) => setNovoComChecklist(e.target.checked)}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    <strong>Abrir checklist de insumos para este chamado</strong>
                    <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
                      Ao salvar, você vai direto para o checklist (materiais e ferramentas), já vinculado ao chamado.
                    </span>
                  </span>
                </label>
              ) : null}

              <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)' }}>
                <i className="bi bi-info-circle me-1" />
                O chamado e registrado em seu nome. Setor vem do cadastro; a filial pode
                ser trocada acima caso voce esteja abrindo em nome de outra unidade.
              </div>
            </div>

            <footer style={{
              padding: '12px 18px',
              borderTop: '1px solid var(--portal-border)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setNovoAberto(false)}
                disabled={novoSalvando}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={salvarNovo}
                disabled={novoSalvando}
                style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
              >
                <i className="bi bi-check2 me-1" />
                {novoSalvando ? 'Abrindo...' : novoComChecklist ? 'Abrir chamado e checklist' : 'Abrir chamado'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
